import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PencilIcon, PlusSmIcon } from '@heroicons/react/outline';
import Trash from '../../svgs/Trash';
import {
  ServiceGroupDataFragment,
  ProviderService,
  useUpdateServiceGroupMutation,
} from '../../../generated/graphql';
import SortableServiceCard from './SortableServiceCard';
import FirstServiceCard from './FirstServiceCard';
import CollapseableContainer from '../CollapseableContainer';
import Button from '../Button';
import ActionButton from '../ActionButton';
import ActionButtonGroup from '../ActionButtonGroup';
import DragHandle from '../../svgs/DragHandle';
import InputGroup from '../InputGroup';
import { customToast } from '../ToastAlert/customToast';
import classNames from 'classnames';

interface SortableServiceGroupProps {
  group: ServiceGroupDataFragment;
  providerPrimaryColor?: string | null;
  canDelete?: boolean;
  onDelete?: (groupId: string) => void;
  handleDeleteService?: (service: ProviderService) => void;
  hasAnyServices?: boolean;
  isServiceDragOver?: boolean;
}

const SortableServiceGroup: React.FC<SortableServiceGroupProps> = ({
  group,
  providerPrimaryColor,
  canDelete = true,
  onDelete,
  handleDeleteService,
  hasAnyServices = false,
  isServiceDragOver = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(group.name);
  const [updateServiceGroup] = useUpdateServiceGroupMutation();

  // Update editedName when group.name changes (e.g., after refetch)
  useEffect(() => {
    if (!isEditing) {
      setEditedName(group.name);
    }
  }, [group.name, isEditing]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const serviceIds = group.services?.map((s) => s.id) || [];

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      setEditedName(group.name);
      setIsEditing(false);
      return;
    }

    try {
      await updateServiceGroup({
        variables: {
          input: {
            id: group.id,
            name: editedName.trim(),
          },
        },
        refetchQueries: ['ServiceGroups'],
      });
      setIsEditing(false);
    } catch (error) {
      customToast.error('Failed to update group name');
      setEditedName(group.name);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(group.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames('mb-6', isDragging && 'opacity-50')}
    >
      {isEditing ? (
        <div className="rounded-xl border border-neutral-75 bg-white p-5">
          <InputGroup
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleSaveName}
            onKeyDown={handleKeyDown}
            labelHidden
            backgroundHidden
            inputSize="small"
            autoFocus
            className="rounded-none border-0 border-b-2 px-0 pb-1 text-body font-medium text-neutral-150"
            containerClassName="mb-0"
          />
        </div>
      ) : (
        <CollapseableContainer
          title={group.name}
          className={classNames(
            'rounded-lg bg-white shadow-sm',
            isServiceDragOver && 'ring-2 ring-green-150 ring-offset-2',
          )}
          defaultOpen={true}
          dragAttributes={attributes}
          dragListeners={listeners}
          TitleLeftComponent={
            <DragHandle className="h-5 w-5 text-neutral-125" />
          }
          ButtonComponent={
            <div className="flex items-center gap-2">
              <ActionButtonGroup size="small" className="mr-2">
                <ActionButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  title="Rename group"
                >
                  <PencilIcon className="text-purple-500 h-4 w-4" />
                </ActionButton>
                {canDelete && onDelete && (
                  <ActionButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        group.displayOrder !== 0 &&
                        (!group.services || group.services.length === 0)
                      ) {
                        onDelete(group.id);
                      }
                    }}
                    title={
                      group.displayOrder === 0
                        ? 'Cannot delete the first group'
                        : group.services && group.services.length > 0
                        ? 'Remove all services before deleting this group'
                        : 'Delete group'
                    }
                    disabled={
                      group.displayOrder === 0 ||
                      (group.services && group.services.length > 0)
                    }
                  >
                    <Trash className="h-4 w-4 text-neutral-125" />
                  </ActionButton>
                )}
              </ActionButtonGroup>
              <Button
                title="Add service"
                theme="primary"
                size="small"
                iconPosition="left"
                IconComponent={PlusSmIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/services/new', {
                    state: {
                      from: location.pathname,
                      serviceGroupId: group.id,
                    },
                  });
                }}
                className="mr-2"
              />
            </div>
          }
        >
          <div
            className={classNames(
              'px-5 pb-5',
              isServiceDragOver && 'bg-green-25/30',
              (!group.services || group.services.length === 0) &&
                'min-h-[120px] w-full',
            )}
          >
            {!group.services || group.services.length === 0 ? (
              // Empty state
              group.displayOrder === 0 && !hasAnyServices ? (
                // First group (position 0) and no services exist anywhere: show clickable FirstServiceCard
                <div className="flex w-full">
                  <FirstServiceCard serviceGroupId={group.id} />
                </div>
              ) : (
                // Other groups or first group when services exist elsewhere: show text message
                <div className="flex min-h-[120px] w-full items-center justify-center py-8">
                  <p className="text-caption text-neutral-125">
                    This group is empty. Add a service to get started.
                  </p>
                </div>
              )
            ) : (
              <SortableContext
                items={serviceIds}
                strategy={horizontalListSortingStrategy}
              >
                <div className="flex flex-wrap justify-start gap-6">
                  {group.services?.map((service) => (
                    <SortableServiceCard
                      key={service.id}
                      service={service as ProviderService}
                      providerPrimaryColor={providerPrimaryColor}
                      handleDeleteService={handleDeleteService}
                    />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>
        </CollapseableContainer>
      )}
    </div>
  );
};

export default SortableServiceGroup;
